import { TFunction } from "i18next";
import * as yup from "yup";

import { ProgramType } from "../Program/ProgramType";
import { OtherEnrollmentConditionId } from "./EnrollmentCondition"
import { OtherFormOfTeachingId, OtherKnowledgeCheckId } from "./OtherDataValues";
import { TeacherValidationScheme } from "./TeacherValidationScheme";



export interface ProgramRequestCorrectionValidationSchemeProps {
  t: TFunction;
}

export const ProgramRequestCorrectionValidationScheme = ({
  t,
}: ProgramRequestCorrectionValidationSchemeProps) => {
  const positiveNumberMsg = t("validations.positiveNumber");
  const requiredMsg = t("validations.required");
  const numberLessThanThousand = t("validations.numberLessThan", { nr: 6000 });
  const positiveNumberOrZeroMsg = t("validations.positiveNumberOrZero");
  const requiredAtLeastOne = t("validations.requiredAtLeastOne");
  const documentTooBig = t("errorCode.Document.TooBig");
  const documentWrongFormat = t("errorCode.Document.WrongFormatPdfWord");

  return yup.object().shape({
    guidedLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanThousand).typeError(requiredMsg),
    independentLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanThousand).typeError(requiredMsg),
    workLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanThousand).typeError(requiredMsg),
    curriculumProgramDocument: yup.mixed().nullable().required(requiredMsg).test('curriculumProgramDocument', documentTooBig, val => {
      if (val === null || (val && val.id !== -1)) {
        return true;
      }
      if (val && val.file.size <= 10485760) {
        return true;
      }
      return false;
    }).test('curriculumProgramDocument', documentWrongFormat, val => {
      if (val) {
        var split = val.fileName.split('.');
        var ext = split[split.length - 1];

        if (ext === "pdf" || ext === "doc" || ext === "docx") {
          return true;
        }
      }
      return false
    }),
    formOfTeachingIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    knowledgeChecksIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    enrollmentConditionsIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    // competences: yup.array().of(yup.object().required(requiredMsg)).min(1),
    // Validate competence names only for types that capture them (the types listed below are skipped).
    // The .when must be at array level: inside .of() programTypeId was undefined, so names were
    // required everywhere and names with stray whitespace failed .trim() and blocked submit.
    competences: yup.array().of(
      yup.object().shape({ name: yup.string().trim().required(requiredMsg) })
    ).when('programTypeId', (programTypeId, schema) => {
      if (programTypeId === ProgramType.BasicEducationPrograms ||
        programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
        programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
        programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
        programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
        programTypeId === ProgramType.BasicArtsPrograms ||
        programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
        programTypeId === ProgramType.CroatianLanguageForForeigners) {
        return yup.array().of(yup.object().nullable());
      }
      return schema;
    }),
    otherEnrollmentConditions: yup.array().of(yup.string()).when("enrollmentConditionsIds", (enrollmentConditionsIds, schema) => {
      if (enrollmentConditionsIds.includes(OtherEnrollmentConditionId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      } else {
        return schema;
      }
    }),
    otherKnowledgeChecks: yup.array().of(yup.string()).when("knowledgeChecksIds", (knowledgeChecksIds, schema) => {
      if (knowledgeChecksIds.includes(OtherKnowledgeCheckId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      }
      else {
        return schema;
      }
    }),
    otherFormsOfTeaching: yup.array().of(yup.string()).when("formOfTeachingIds", (formOfTeachingIds, schema) => {
      if (formOfTeachingIds.includes(OtherFormOfTeachingId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      }
      else {
        return schema;
      }
    }),
    teachers: yup.array().of(
      TeacherValidationScheme({ t })
    ),
  });
};



